import ApiClient from '../../core/ApiClient';
import { resolveDefaultVehicleCountryCode } from '../../utils/languageDefaultCountry.js';
import {
    getCategoryCanonicalDescription,
    getCategoryDescription,
    getCategoryId,
    normalizeDurationKey
} from '../../components/BuyVignette/config/durationKeywords.js';
import {
    fuelTypeOptionSortIndex,
    fuelProductDisplayTitle,
    vignetteFuelRowOrderIndex
} from '../../utils/fuelTypeProduct.js';
import {
    fetchProductCatalog,
    flattenProductsFromCategories,
    normalizeProductCategoriesResponse
} from '../../utils/productCatalog.js';
import { getProductDurationDays } from '../../utils/productDuration.js';
import { normalizeFlagEntity } from '../../utils/flagUrl.js';
import { getStartDateRange } from '../../utils/startDateRange.js';
import { normalizeLicensePlatesInForm } from '../../utils/licensePlate.js';
import { readClientId, readGclid, readMsclkid, readSessionId, sha256HexEmail } from '../../analytics/sendMakePurchaseWebhook.js';

async function buildOrderApiTags(email) {
    return {
        gclid: readGclid(),
        msclkid: readMsclkid(),
        sid: readSessionId(),
        cid: readClientId(),
        site: 'vignetteslovenia.si',
        /** SHA-256 hex of trimmed email; same algorithm as Make.com purchase webhook. */
        email: await sha256HexEmail(email),
    };
}

export const STEP_SELECT_VIGNETTE = 1;
const STEP_NAMES = ['select-vignette', 'enter-details', 'payment'];
const DISALLOWED_FORM_KEYS = new Set(['name']);
const FORM_KEY_ALIASES = {
    invoice_business_name: 'invoice_company_name',
    invoice_vat_number: 'invoice_vat',
};

const getDefaultFormData = () => ({
    vehicle_type: null,
    /** Locale-stable vignette duration group; matches product.category.id when API provides it. */
    category_id: null,
    product_id: null,
    crossing_type: null,
    selected_duration: null,
    start_date: '',

    plate_country: '',
    license_plate: '',
    license_plate_confirmation: '',
    email: '',
    email_confirmation: '',

    need_an_invoice: false,
    invoice_name: '',
    invoice_surname: '',
    invoice_company_name: '',
    invoice_vat: { country: '', number: '' },
    invoice_city: '',
    invoice_address: '',
    invoice_zipcode: '',
    invoice_country: '',

    payment_method: null,
    terms_condition: false,
    confirm_entered_data: false
});

const STEP_FIELDS = {
    step1: ['start_date'],
    step2: [
        'product_id',
        'plate_country',
        'license_plate',
        'license_plate_confirmation',
        'email',
        'email_confirmation',
        'need_an_invoice',
        'invoice_name',
        'invoice_surname',
        'invoice_company_name',
        'invoice_vat',
        'invoice_city',
        'invoice_address',
        'invoice_zipcode',
        'invoice_country'
    ],
    step3: ['payment_method', 'confirm_entered_data', 'terms_condition']
};

const STEP_TO_API_NUMBER = {
    step1: '1',
    step2: '2',
    step3: '3'
};

/** Keys used by buy-vignette flow; cleared when checkout completes so the next order starts at step 1. */
const BUY_FLOW_LOCAL_STORAGE_KEYS = [
    'vignetteFormData',
    'currentStep',
    'formData',
    'product_id',
];

/**
 * Mirror of the last value `syncVehicleCountryToLanguage` wrote into formData.plate_country.
 * Drives manual-override detection: when the live plate_country differs from this mirror
 * (or the mirror is empty), we assume the user picked the country themselves and the
 * language-driven autosync must NOT overwrite it. Cleared by `markPlateCountryManual`.
 */
const AUTO_SELECTED_PLATE_COUNTRY_KEY = 'autoSelectedPlateCountry';

/** Debounce key for ShoppingCart.mounted force-sync: holds the lang.code last applied via autosync. */
const CART_LANG_APPLIED_KEY = 'cartLangCodeApplied';

function getStoredAutoSelectedPlateCountry() {
    try {
        if (typeof localStorage === 'undefined') return '';
        return String(localStorage.getItem(AUTO_SELECTED_PLATE_COUNTRY_KEY) || '').trim();
    } catch (e) {
        return '';
    }
}

function setStoredAutoSelectedPlateCountry(code) {
    try {
        if (typeof localStorage === 'undefined') return;
        const normalized = String(code || '').trim();
        if (normalized) {
            localStorage.setItem(AUTO_SELECTED_PLATE_COUNTRY_KEY, normalized);
        } else {
            localStorage.removeItem(AUTO_SELECTED_PLATE_COUNTRY_KEY);
        }
    } catch (e) {
        /* ignore quota / disabled storage */
    }
}

function clearStoredAutoSelectedPlateCountry() {
    setStoredAutoSelectedPlateCountry('');
}

/**
 * True when the live plate_country was NOT placed there by the most recent autosync.
 * Cases:
 *   - mirror empty + current set  → user picked it (or restored from a pre-mirror session)
 *   - mirror set, differs from current → user changed country after autosync ran
 * Empty current is never considered an override (autosync should be allowed to fill it).
 */
function hasManualPlateCountryOverride(formData) {
    const current = String(formData?.plate_country || '').trim();
    if (!current) return false;
    const autoSelected = getStoredAutoSelectedPlateCountry();
    return !autoSelected || autoSelected !== current;
}

/** Normalize root `lang.code` (e.g. "RO-MD") into the form expected by LANGUAGE_TO_COUNTRY. */
function getNormalizedLanguageCode(rootState) {
    const raw = String(rootState?.lang?.code || '').toLowerCase().trim();
    if (raw === 'ro-md' || raw === 'moldova-ro' || raw === 'ro_md') return 'md';
    return raw;
}

function getValidationFieldsForStep(state, stepKey) {
    const apiStep = STEP_TO_API_NUMBER[stepKey];
    const apiFields = (state.formFields || [])
        .filter((f) => String(f.step) === String(apiStep))
        .map((f) => f.key)
        .filter((key) => !DISALLOWED_FORM_KEYS.has(key));

    // Prefer server-driven step fields when available.
    if (apiFields.length) return apiFields;
    return (STEP_FIELDS[stepKey] || []).filter((key) => !DISALLOWED_FORM_KEYS.has(key));
}

/**
 * GET /products sometimes returns category buckets:
 * { id: 78, translated: {...}, products: [ { id: 19, ... }, ... ] }.
 * The storefront expects a flat list of products with product.category set.
 */
function flattenProductsApiData(data) {
    if (!Array.isArray(data)) return [];
    const out = [];
    for (const row of data) {
        if (!row || typeof row !== 'object') continue;
        const nested = row.products;
        if (Array.isArray(nested) && nested.length) {
            const parentCategory = {
                id: row.id,
                path: row.path,
                thumbnail: row.thumbnail,
                translated: row.translated,
                description: row.description,
            };
            for (const p of nested) {
                if (!p || typeof p !== 'object') continue;
                const childCat = p.category && typeof p.category === 'object' ? p.category : {};
                out.push({
                    ...p,
                    category: {
                        ...parentCategory,
                        ...childCat,
                    },
                });
            }
            continue;
        }
        out.push(row);
    }
    return out;
}

function normalizeErrorFieldKey(rawKey) {
    const key = String(rawKey || '');
    let base = key;
    if (key.startsWith('form.main.')) base = key.replace('form.main.', '');
    else if (key.startsWith('main.')) base = key.replace('main.', '');
    else if (key.startsWith('form.')) base = key.replace('form.', '');
    if (base === 'vehicle_registration_country') base = 'plate_country';
    return normalizeFormKey(base);
}

function validationErrorFromResponseData(responseData) {
    const errors = responseData?.errors;
    if (!errors || typeof errors !== 'object' || !Object.keys(errors).length) return null;

    const validationErrors = {};
    Object.keys(errors).forEach((key) => {
        const fieldKey = normalizeErrorFieldKey(key);
        validationErrors[fieldKey] = true;
    });

    const err = new Error(responseData.message || 'Validation failed');
    err.validationErrors = validationErrors;
    return err;
}

function normalizeFormKey(key) {
    const raw = String(key || '');
    const [base, ...rest] = raw.split('.');
    const normalizedBase = FORM_KEY_ALIASES[base] || base;
    return [normalizedBase, ...rest].filter(Boolean).join('.');
}

function normalizeFormKeys(payload = {}) {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return {};
    return Object.entries(payload).reduce((acc, [key, value]) => {
        const normalizedKey = normalizeFormKey(key);
        if (value === undefined) return acc;
        if (normalizedKey === 'need_an_invoice') {
            acc[normalizedKey] = normalizeBooleanFlag(value);
            return acc;
        }
        if (!Object.prototype.hasOwnProperty.call(acc, normalizedKey) || key === normalizedKey) {
            acc[normalizedKey] = value;
        }
        return acc;
    }, {});
}

function normalizeBooleanFlag(value) {
    if (value === true || value === 1) return true;
    if (value === false || value === 0 || value == null) return false;
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (['1', 'true', 'on', 'yes'].includes(normalized)) return true;
        if (['', '0', 'false', 'off', 'no', 'null', 'undefined'].includes(normalized)) return false;
    }
    return Boolean(value);
}

function invoiceFieldsToClear() {
    return [
        'invoice_name',
        'invoice_surname',
        'invoice_company_name',
        'invoice_vat',
        'invoice_city',
        'invoice_address',
        'invoice_zipcode',
        'invoice_country',
    ];
}

function clearInvoicePayload(targetForm = {}) {
    invoiceFieldsToClear().forEach((key) => {
        delete targetForm[key];
    });
    return targetForm;
}

function normalizeFormFields(formFields) {
    if (!Array.isArray(formFields)) return [];
    return formFields.map((field) => {
        const f = { ...field };
        f.key = normalizeFormKey(f.key);
        return f;
    });
}

/** Same rules as EnterDetails `categoryProducts` filter (fuel / vignette rows). */
function productBelongsToSelectedCategory(product, formData) {
    const fd = formData || {};
    const cid = fd.category_id;
    if (cid != null && cid !== '') {
        const pid = getCategoryId(product);
        if (pid == null || pid === '') return false;
        return String(pid) === String(cid);
    }
    const vt = String(fd.vehicle_type || '').toLowerCase().trim();
    if (!vt) return false;
    const localized = String(getCategoryDescription(product) || '').toLowerCase().trim();
    if (localized && localized === vt) return true;
    const canon = String(getCategoryCanonicalDescription(product) || '').toLowerCase().trim();
    if (canon && canon === vt) return true;
    const nk = (s) => normalizeDurationKey(s) || String(s || '').toLowerCase().trim();
    return nk(localized) === nk(vt) || nk(canon) === nk(vt);
}

function extractProductDuration(product) {
    /**
     * Canonical days from API (count × duration_type). Falls back to URL slug
     * for legacy entries where duration_type is missing.
     */
    const days = getProductDurationDays(product);
    if (days > 0) return days;
    const match = product?.path && String(product.path).match(/(\d+)-day/);
    return match ? parseInt(match[1], 10) : 0;
}

/** Sorted fuel products for current vignette category — mirrors EnterDetails `categoryProducts` order. */
function getSortedCategoryProducts(products, formData) {
    const list = Array.isArray(products) ? products : [];
    if (!list.length) return [];
    const fd = formData || {};
    const hasCategory =
        (fd.category_id != null && fd.category_id !== '')
        || String(fd.vehicle_type || '').trim();
    if (!hasCategory) return [];
    return list
        .filter((p) => productBelongsToSelectedCategory(p, fd))
        .sort((a, b) => {
            const byId = vignetteFuelRowOrderIndex(a) - vignetteFuelRowOrderIndex(b);
            if (byId !== 0) return byId;
            return (
                fuelTypeOptionSortIndex(fuelProductDisplayTitle(a))
                - fuelTypeOptionSortIndex(fuelProductDisplayTitle(b))
            );
        });
}

function normalizeInvoiceVatNumber(targetForm = {}) {
    if (!Object.prototype.hasOwnProperty.call(targetForm, 'invoice_vat')) return targetForm;
    const vatValue = targetForm.invoice_vat;
    const sanitizeVat = (value) =>
        String(value ?? '')
            .trim()
            .replace(/\s+/g, '')
            .toUpperCase();
    if (vatValue && typeof vatValue === 'object' && !Array.isArray(vatValue)) {
        targetForm.invoice_vat = sanitizeVat(vatValue.number);
        return targetForm;
    }
    targetForm.invoice_vat = sanitizeVat(vatValue);
    return targetForm;
}

function invoiceVatPayloadPatch(targetForm = {}) {
    if (!Object.prototype.hasOwnProperty.call(targetForm, 'invoice_vat')) return {};
    return { invoice_vat: String(targetForm.invoice_vat ?? '') };
}

function invoiceVatNumberForOrder(form = {}) {
    const value = form.invoice_vat;
    if (!value) return '';
    if (typeof value === 'object' && !Array.isArray(value)) {
        return String(value.number ?? '').trim();
    }
    return String(value ?? '').trim();
}

function cleanEmptyObjectValues(obj = {}) {
    return Object.fromEntries(
        Object.entries(obj).filter(([, value]) => value !== undefined && value !== null && value !== '')
    );
}

function buildBillingData(form = {}) {
    return cleanEmptyObjectValues({
        first_name: form.invoice_name,
        last_name: form.invoice_surname,
        country: form.invoice_country,
        city: form.invoice_city,
        address: form.invoice_address,
        zipcode: form.invoice_zipcode,
        company_name: form.invoice_company_name
    });
}

function productCatalogLabels(categories = [], categoryId = null, productId = null) {
    const category = (Array.isArray(categories) ? categories : []).find(
        (item) => String(item?.id) === String(categoryId)
    );
    const product = (category?.products || []).find(
        (item) => String(item?.id) === String(productId)
    );

    return {
        categoryTitle: category?.translated?.title ?? category?.title ?? '',
        productTitle: product?.translated?.title ?? product?.title ?? product?.name ?? ''
    };
}

function buildOrderMainForm(form = {}, needInvoice = false) {
    const main = {
        name: form.name ?? '',
        email: form.email ?? '',
        email_confirmation: form.email_confirmation ?? '',
        phone: form.phone,
        start_date: form.start_date ?? '',
        end_date: form.end_date ?? '',
        vehicle_type: form.vehicle_type ?? '',
        crossing_type: form.crossing_type ?? '',
        license_plate: form.license_plate ?? '',
        license_plate_confirmation: form.license_plate_confirmation ?? '',
        plate_country: form.plate_country ?? form.vehicle_registration_country ?? '',
        vehicle_registration_country: form.plate_country ?? form.vehicle_registration_country ?? '',
        need_an_invoice: needInvoice
    };

    if (needInvoice) {
        Object.assign(main, {
            invoice_name: form.invoice_name ?? '',
            invoice_surname: form.invoice_surname ?? '',
            invoice_country: form.invoice_country ?? '',
            invoice_company_name: form.invoice_company_name ?? '',
            invoice_city: form.invoice_city ?? '',
            invoice_address: form.invoice_address ?? '',
            invoice_zipcode: form.invoice_zipcode ?? '',
            invoice_vat_number: invoiceVatNumberForOrder(form)
        });
    }

    return main;
}

function extractVatValidationError(responseData) {
    const message = String(responseData?.message ?? '');
    const exception = String(responseData?.exception ?? '');
    const looksLikeVatTypeError =
        exception === 'TypeError'
        && /VatValidator::validate/.test(message)
        && /must be of type string,\s*array given/i.test(message);

    const isViesConcurrentLimitError =
        /ViesException/.test(exception)
        && /MS_MAX_CONCURRENT_REQ/i.test(message);

    if (!looksLikeVatTypeError && !isViesConcurrentLimitError) return null;

    const userMessage = looksLikeVatTypeError
        ? 'Please enter a valid VAT number (e.g. DE129273398).'
        : 'VAT validation service is busy right now. Please try again in 1-2 minutes.';
    const err = new Error(userMessage);
    err.validationErrors = {
        invoice_vat: userMessage,
        'invoice_vat.number': userMessage,
    };
    return err;
}

/**
 * Some /forms/fields responses omit invoice_vat. Inject a step-2 text field
 * between business name and the next invoice field so it appears in EnterDetails
 * and is included in API-driven validation keys.
 */
function ensureInvoiceVatField(formFields) {
    if (!Array.isArray(formFields) || formFields.some((f) => f.key === 'invoice_vat')) {
        return formFields;
    }
    const step2 = formFields.filter((f) => String(f.step) === '2');
    const sorted = [...step2].sort((a, b) => (Number(a.index) || 0) - (Number(b.index) || 0));
    const businessPos = sorted.findIndex((f) => f.key === 'invoice_company_name');
    const business = businessPos >= 0 ? sorted[businessPos] : null;
    const next = businessPos >= 0 ? sorted[businessPos + 1] : null;

    let index = 100;
    if (business && next) {
        const bi = Number(business.index) || 0;
        const ni = Number(next.index) || bi + 10;
        index = bi + (ni - bi) / 2;
    } else if (business) {
        index = Number(business.index) + 5;
    } else {
        const invoiceBlock = sorted.filter((f) => f.key && f.key.startsWith('invoice_'));
        const last = invoiceBlock[invoiceBlock.length - 1];
        if (last) index = Number(last.index) + 5;
    }

    const vatField = {
        key: 'invoice_vat',
        type: 'vat_composite',
        step: '2',
        section_value: 'invoice',
        index,
        translated: {
            label: 'VAT number',
            placeholder: 'e.g. DE129273398',
        },
    };
    return [...formFields, vatField];
}

function pickStepData(formData, fields) {
    const result = {};
    fields.forEach((key) => {
        result[key] = formData[key];
    });
    return result;
};

/** One shared load for ShoppingCart, BuyVignette, VignettePrices, etc. */
let initialDataLoaded = false;
let initialDataLoadPromise = null;

/**
 * Single-flight cache for `hydrateFromOrderToken`. Reuses the in-flight promise
 * when the same token is hydrated again (HMR remount, fast double-mount, etc.)
 * to avoid duplicate `POST /orders/request-data/<uuid>` calls.
 */
let hydrateOrderTokenPromise = null;
let hydrateOrderTokenLastUuid = null;

/**
 * Effective country for GET /payments/methods/sorted — driven by the user's
 * plate_country selection from formData. Language is only used as a fallback
 * during the very first load when plate_country hasn't been resolved yet.
 *
 * Tracks the country value the cached methods correspond to so subsequent
 * `reloadPaymentMethods()` calls become no-ops while plate_country stays
 * the same.
 */
let paymentMethodsFetchedForPlateKey = null;

function paymentMethodValue(method) {
    return String(method?.value ?? method?.code ?? '').trim().toLowerCase();
}

function uniquePaymentMethods(methods) {
    const seen = new Set();
    return (Array.isArray(methods) ? methods : []).filter((method) => {
        const value = paymentMethodValue(method);
        if (!value || seen.has(value)) return false;
        seen.add(value);
        return true;
    });
}

function normalizePaymentMethodsForCountry(methods, country) {
    const normalizedCountry = String(country || '').trim().toUpperCase();
    const main = uniquePaymentMethods(methods?.main || []);
    const other = uniquePaymentMethods(methods?.other || []);

    if (normalizedCountry !== 'NL') {
        return { main, other };
    }

    const all = [...main, ...other];
    const ideal = all.find((method) => paymentMethodValue(method) === 'ideal');

    if (!ideal) {
        return { main, other };
    }

    return {
        main: [
            ideal,
            ...main.filter((method) => paymentMethodValue(method) !== 'ideal')
        ],
        other: other.filter((method) => paymentMethodValue(method) !== 'ideal')
    };
}

/**
 * Resolve the effective country code to send in `data.country`.
 * Priority:
 *   1. `plate_country` from formData (user's chosen vehicle country).
 *   2. UI language mapping via LANGUAGE_TO_COUNTRY — only used during the
 *      initial load when plate_country is still empty.
 *   3. Hardcoded 'CZ' as last resort.
 */
function resolvePaymentCountryCode(plateCountry, langCode, countries) {
    const direct = String(plateCountry || '').trim().toUpperCase();
    if (direct) return direct;
    const langCountry = resolveDefaultVehicleCountryCode(langCode, countries);
    return String(langCountry || 'CZ').toUpperCase();
}

async function loadPaymentMethodsData(commit, plateCountry, langCode, countries) {
    const effectiveKey = resolvePaymentCountryCode(plateCountry, langCode, countries);
    const api = new ApiClient();
    const res = await api.request({
        path: '/payments/methods/sorted',
        data: { country: effectiveKey }
    });
    const raw = res?.data?.data ?? res?.data ?? {};
    const { main, other } = normalizePaymentMethodsForCountry({
        main: Array.isArray(raw?.main) ? raw.main : [],
        other: Array.isArray(raw?.other) ? raw.other : []
    }, effectiveKey);
    commit('SET_PAYMENT_METHODS', { main, other });
    paymentMethodsFetchedForPlateKey = effectiveKey;
    return [...main, ...other];
}

function selectedCurrencyCodeFromStorage() {
    if (typeof window === 'undefined' || !window.localStorage) return '';
    return String(localStorage.getItem('currency') ?? '')
        .trim()
        .toUpperCase();
}

/** Selected checkout currency code for POST /orders/make and /orders/amount (e.g. `"EUR"`). */
function resolveCheckoutCurrencyCode(rootGetters) {
    const code = String(rootGetters?.['currency/currentCurrency']?.code ?? '')
        .trim()
        .toUpperCase();
    return code || null;
}

/** POST /orders/amount — `{ data: { amount, sub_amount, payment_method_fee, currency, products } }`. */
function extractOrderAmountFromApiEnvelope(res) {
    const body = res?.data;
    if (!body || typeof body !== 'object') return null;
    const nodes = [body, body.data, body.data?.data, body.result, body.result?.data].filter(
        (n) => n != null && typeof n === 'object'
    );
    for (const node of nodes) {
        if (
            node.amount != null
            || node.sub_amount != null
            || node.payment_method_fee != null
        ) {
            return node;
        }
    }
    return null;
}

export const formModule = {
    namespaced: true,

    state: {
        formData: getDefaultFormData(),
        step: STEP_SELECT_VIGNETTE,
        countries: [],
        /** Canonical catalog from /products/categories. */
        productCategories: [],
        /** Back-compat flat list derived from productCategories. */
        products: [],
        formFields: [],
        /** Back-compat flat list: main + other */
        paymentMethods: [],
        /** Sorted methods from /payments/methods/sorted */
        paymentMethodsMain: [],
        paymentMethodsOther: [],
        /** Latest POST /orders/amount for selected payment method. */
        orderAmount: null,
        orderAmountLoading: false,
        loadingCount: 0,
        orderData: null,
        stepValidationErrors: {},
        allowedFormKeys: [],
        /** From GET /products: res.data.items (array or object of arrays) — fuel icons per product_id */
        productIconItems: [],
        /** Back-compat alias for productCategories. */
        categorizedProducts: [],
        /** root `lang.id` used for last successful /countries fetch (avoids stale list after language change). */
        countriesFetchLanguageId: null,
    },

    mutations: {
        SET_FORM_DATA(state, payload) {
            const { minDate: today, maxDate } = getStartDateRange();

            const sanitizedPayload = normalizeFormKeys(Object.fromEntries(
                Object.entries(payload || {}).filter(([key]) => !DISALLOWED_FORM_KEYS.has(key))
            ));

            if (Object.prototype.hasOwnProperty.call(sanitizedPayload, 'vehicle_registration_country')) {
                if (sanitizedPayload.plate_country == null || sanitizedPayload.plate_country === '') {
                    sanitizedPayload.plate_country = sanitizedPayload.vehicle_registration_country ?? '';
                }
                delete sanitizedPayload.vehicle_registration_country;
            }

            if (
                sanitizedPayload.start_date
                && (sanitizedPayload.start_date < today || sanitizedPayload.start_date > maxDate)
            ) {
                sanitizedPayload.start_date = today;
            }

            if (!sanitizedPayload.start_date && !state.formData.start_date) {
                sanitizedPayload.start_date = today;
            }

            if (
                Object.prototype.hasOwnProperty.call(sanitizedPayload, 'category_id')
                && sanitizedPayload.category_id !== undefined
            ) {
                sanitizedPayload.vehicle_type = sanitizedPayload.category_id;
            }

            if (
                Object.prototype.hasOwnProperty.call(sanitizedPayload, 'product_id')
                && sanitizedPayload.product_id !== undefined
            ) {
                sanitizedPayload.crossing_type = sanitizedPayload.product_id;
            }

            state.formData = {
                ...state.formData,
                ...sanitizedPayload
            };
        },
        SET_ALLOWED_FORM_KEYS(state, keys) {
            state.allowedFormKeys = [...new Set((keys || []).map(normalizeFormKey))];
        },
        CLEAR_FORM_DATA(state) {
            try {
                BUY_FLOW_LOCAL_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
                /**
                 * Drop autosync mirror + applied-lang stamp on a hard reset so the next session
                 * starts with autosync enabled (otherwise an old `autoSelectedPlateCountry=DE`
                 * would look like a manual override on the fresh form and disable autosync).
                 */
                localStorage.removeItem(AUTO_SELECTED_PLATE_COUNTRY_KEY);
                localStorage.removeItem(CART_LANG_APPLIED_KEY);
            } catch (e) {}
            state.formData = getDefaultFormData();
            state.step = STEP_SELECT_VIGNETTE;
            state.orderAmount = null;
            state.orderAmountLoading = false;
        },

        SET_STEP(state, payload) {
            const n = typeof payload === 'number' ? payload : (STEP_NAMES.indexOf(payload) + 1) || STEP_SELECT_VIGNETTE;
            state.step = n >= 1 && n <= 3 ? n : STEP_SELECT_VIGNETTE;
        },

        SET_COUNTRIES(state, countries) {
            const list = Array.isArray(countries) ? countries : [];
            state.countries = list.map((country) => normalizeFlagEntity(country));
        },

        SET_COUNTRIES_FETCH_LANGUAGE_ID(state, languageId) {
            state.countriesFetchLanguageId =
                languageId === undefined || languageId === null || languageId === ''
                    ? null
                    : languageId;
        },

        SET_PRODUCTS(state, products) {
            state.products = products;
            // console.log('[Vuex] products загружены:', state.products);
        },

        SET_PRODUCT_CATALOG(state, categories) {
            const normalizedCategories = normalizeProductCategoriesResponse(categories);
            state.productCategories = normalizedCategories;
            state.categorizedProducts = normalizedCategories;
            state.products = flattenProductsFromCategories(normalizedCategories);
            state.productIconItems = [];
        },

        SET_PRODUCT_ICON_ITEMS(state, items) {
            state.productIconItems = Array.isArray(items) ? items : [];
        },

        SET_CATEGORIZED_PRODUCTS(state, rows) {
            const normalizedCategories = normalizeProductCategoriesResponse(rows);
            state.productCategories = normalizedCategories;
            state.categorizedProducts = normalizedCategories;
            state.products = flattenProductsFromCategories(normalizedCategories);
        },

        SET_FORM_FIELDS(state, formFields) {
            state.formFields = formFields;
            // console.log('[Vuex] formFields загружены:', state.formFields);
        },

        SET_PAYMENT_METHODS(state, methods) {
            // Supports either legacy array or new { main: [], other: [] } payload.
            if (Array.isArray(methods)) {
                state.paymentMethodsMain = methods;
                state.paymentMethodsOther = [];
                state.paymentMethods = methods;
                return;
            }
            const main = Array.isArray(methods?.main) ? methods.main : [];
            const other = Array.isArray(methods?.other) ? methods.other : [];
            state.paymentMethodsMain = main;
            state.paymentMethodsOther = other;
            state.paymentMethods = [...main, ...other];
            // console.log('[Vuex] paymentMethods загружены:', state.paymentMethods);
        },

        SET_ORDER_DATA(state, payload) {
            state.orderData = payload;
            // console.log('[Vuex] orderData загружены:', state.orderData);
        },

        SET_ORDER_AMOUNT(state, payload) {
            state.orderAmount = payload && typeof payload === 'object' ? payload : null;
        },

        SET_ORDER_AMOUNT_LOADING(state, loading) {
            state.orderAmountLoading = !!loading;
        },

        SET_STEP_VALIDATION_ERRORS(state, payload = {}) {
            const step = payload.step || '';
            if (!step) return;
            state.stepValidationErrors = {
                ...state.stepValidationErrors,
                [step]: payload.errors && typeof payload.errors === 'object' ? payload.errors : {}
            };
        },
        CLEAR_STEP_VALIDATION_ERRORS(state, step) {
            if (!step) {
                state.stepValidationErrors = {};
                return;
            }
            const next = { ...state.stepValidationErrors };
            delete next[step];
            state.stepValidationErrors = next;
        }
    },

    actions: {
        /**
         * Align license-plate country with the current site language (see LANGUAGE_TO_COUNTRY).
         * Respects manual selection: if the user picked a country via DropdownField
         * (markPlateCountryManual cleared the mirror), the current value is preserved
         * even when the UI language changes.
         *
         * @param {{ force?: boolean }} [options] kept for caller compatibility; manual override
         *        still wins regardless of force. When the current value already matches the
         *        target (auto path), we just refresh the mirror + applied-lang stamp.
         */
        async syncVehicleCountryToLanguage({ state, commit, rootState, dispatch }, options = {}) {
            const countries = state.countries || [];
            if (!countries.length) return state.formData;

            const langCode = getNormalizedLanguageCode(rootState);
            const targetCountry = resolveDefaultVehicleCountryCode(langCode, countries);
            if (!targetCountry) return state.formData;

            const current = state.formData?.plate_country || '';

            /**
             * Manual-override bail-out. Still record the language as "applied" so the
             * ShoppingCart.mounted force-sync (compares lang.code with cartLangCodeApplied)
             * does not re-run on every navigation — manual choice should be sticky across
             * language switches, not endlessly re-checked.
             */
            if (hasManualPlateCountryOverride(state.formData)) {
                await dispatch('saveAppliedLanguageCode', langCode);
                return state.formData;
            }

            if (current === targetCountry) {
                setStoredAutoSelectedPlateCountry(targetCountry);
                await dispatch('saveAppliedLanguageCode', langCode);
                return state.formData;
            }

            /**
             * Autosync path — license_plate is normally cleared because its format is tied
             * to the registration country (country-specific plate formats, e.g. "1AB 2345" vs "B-MW 8547").
             *
             * Exception: when `plate_country` was empty we are assigning a country for the
             * first time (no country→country switch), so an already-entered plate is preserved.
             * This keeps the plate typed in the home-page search (HomeLicensePlateSearch sets
             * license_plate but no country) intact after navigating to buy-vignette.
             */
            const isFirstCountryAssignment = !current;
            commit('SET_FORM_DATA', {
                plate_country: targetCountry,
                ...(isFirstCountryAssignment
                    ? {}
                    : { license_plate: '', license_plate_confirmation: '' })
            });
            setStoredAutoSelectedPlateCountry(targetCountry);
            await dispatch('saveAppliedLanguageCode', langCode);
            await dispatch('saveDataToLocalStorage');
            return state.formData;
        },

        /**
         * Dispatched after the user explicitly picks a country in the plate-country dropdown
         * (see DropdownField selectOption → ShoppingCart/EnterDetails handleDropdownFieldChange).
         * Clearing the mirror is the signal: from now on `hasManualPlateCountryOverride` returns
         * true for the current plate_country and `syncVehicleCountryToLanguage` will bail out.
         */
        async markPlateCountryManual({ dispatch }) {
            clearStoredAutoSelectedPlateCountry();
            await dispatch('saveDataToLocalStorage');
        },

        /** Persist the language code most recently processed by autosync (drives the force-sync gate). */
        async saveAppliedLanguageCode(_, langCode) {
            const normalizedLang = String(langCode || '').toLowerCase().trim();
            try {
                if (normalizedLang) {
                    localStorage.setItem(CART_LANG_APPLIED_KEY, normalizedLang);
                }
            } catch (e) { /* ignore */ }
            return normalizedLang;
        },

        loadSavedData({ commit }) {
            const savedData = localStorage.getItem("vignetteFormData");

            if (savedData) {
                try {
                    const parsed = JSON.parse(savedData);
                    const sanitizedParsed = normalizeFormKeys(Object.fromEntries(
                        Object.entries(parsed || {}).filter(([key]) => !DISALLOWED_FORM_KEYS.has(key))
                    ));
                    const merged = {
                        ...getDefaultFormData(),
                        ...sanitizedParsed,
                    };

                    if (Object.prototype.hasOwnProperty.call(merged, 'vehicle_registration_country')) {
                        if (merged.plate_country == null || merged.plate_country === '') {
                            merged.plate_country = merged.vehicle_registration_country ?? '';
                        }
                        delete merged.vehicle_registration_country;
                    }

                    const { minDate: today, maxDate } = getStartDateRange();

                    if (merged.start_date && (merged.start_date < today || merged.start_date > maxDate)) {
                        merged.start_date = today;
                    }

                    if (!merged.start_date) {
                        merged.start_date = today;
                    }
                    merged.need_an_invoice = normalizeBooleanFlag(merged.need_an_invoice);

                    // Legacy: flat invoice_vat string → composite { country, number }
                    if (
                        Object.prototype.hasOwnProperty.call(merged, 'invoice_vat')
                        && merged.invoice_vat
                        && (typeof merged.invoice_vat !== 'object' || Array.isArray(merged.invoice_vat))
                    ) {
                        merged.invoice_vat = {
                            country: '',
                            number: String(merged.invoice_vat),
                        };
                    }
                    if (
                        !merged.invoice_vat
                        || typeof merged.invoice_vat !== 'object'
                        || Array.isArray(merged.invoice_vat)
                    ) {
                        merged.invoice_vat = { country: '', number: '' };
                    } else {
                        merged.invoice_vat = {
                            country: merged.invoice_vat.country ?? '',
                            number: merged.invoice_vat.number ?? '',
                        };
                    }
                    commit("SET_FORM_DATA", merged);
                } catch (e) {
                    console.error("Failed to parse saved data", e);
                }
            }
        },

        saveDataToLocalStorage({ state }) {
            try {
                localStorage.setItem("vignetteFormData", JSON.stringify(state.formData));
            } catch (e) {
                console.error("Failed to save data to localStorage", e);
            }
        },

        /**
         * Single source of truth for default fuel/vignette row product_id (EnterDetails fuel list).
         * Mirrors EnterDetails categoryProducts sort; does not override a valid existing product_id
         * (including from localStorage). Clears stale ids that no longer match the category.
         */
        async autoSelectDefaultProduct({ state, commit, dispatch }) {
            const products = state.products || [];
            if (!products.length) return;

            const sorted = getSortedCategoryProducts(products, state.formData);
            const rawId = state.formData.product_id;
            const hasId = rawId != null && rawId !== '';

            if (!sorted.length) {
                if (hasId) {
                    commit('SET_FORM_DATA', {
                        product_id: null,
                        selected_duration: null
                    });
                    await dispatch('saveDataToLocalStorage');
                }
                return;
            }

            if (hasId) {
                const stillValid = sorted.some(
                    (p) => p.id === rawId || String(p.id) === String(rawId)
                );
                if (stillValid) return;
                commit('SET_FORM_DATA', {
                    product_id: null,
                    selected_duration: null
                });
            }

            const fd = state.formData;
            if (fd.product_id != null && fd.product_id !== '') return;

            const first = sorted[0];
            const duration = extractProductDuration(first);
            const catId = getCategoryId(first);
            const payload = {
                product_id: first.id,
                selected_duration: duration || null,
                category_id: catId != null && catId !== '' ? catId : fd.category_id
            };
            commit('SET_FORM_DATA', payload);
            await dispatch('saveDataToLocalStorage');
        },

        async validateStep({ state }, stepKey) {
            let fields = getValidationFieldsForStep(state, stepKey);

            if (!fields) {
                throw new Error(`Unknown step: ${stepKey}`);
            }
            fields = [...new Set(fields.map(k => {
                const key = k === 'vehicle_registration_country' ? 'plate_country' : k;
                return normalizeFormKey(key);
            }))];
            if (stepKey === 'step2' && !fields.includes('invoice_vat')) {
                fields = [...fields, 'invoice_vat'];
            }
            if (stepKey === 'step2' && !fields.includes('plate_country')) {
                fields = [...fields, 'plate_country'];
            }

            const stepForm = pickStepData(state.formData, fields);
            const filteredStepForm = Object.keys(stepForm)
                .filter(key => state.allowedFormKeys.includes(key))
                .filter(key => !DISALLOWED_FORM_KEYS.has(key))
                .reduce((obj, key) => {
                    obj[key] = stepForm[key];
                    return obj;
                }, {});
            if (stepKey === 'step2') {
                filteredStepForm.plate_country =
                    state.formData.plate_country
                    ?? filteredStepForm.plate_country
                    ?? '';
                filteredStepForm.need_an_invoice = normalizeBooleanFlag(
                    state.formData.need_an_invoice ?? filteredStepForm.need_an_invoice
                );
            }

            if (stepKey === 'step2' && !normalizeBooleanFlag(filteredStepForm.need_an_invoice)) {
                clearInvoicePayload(filteredStepForm);
            }
            normalizeInvoiceVatNumber(filteredStepForm);
            normalizeLicensePlatesInForm(filteredStepForm);

            const res = await new ApiClient().request({
                method: 'POST',
                path: '/forms/validate',
                data: {
                    step: stepKey,
                    // form: filteredStepForm
                    form: {
                        main: {
                            ...filteredStepForm,
                            ...invoiceVatPayloadPatch(filteredStepForm),
                        }
                    }
                }
            });

            const responseData = res?.data;

            const validationErr = validationErrorFromResponseData(responseData);
            if (validationErr) throw validationErr;

            const vatTypeErr = extractVatValidationError(responseData);
            if (vatTypeErr) {
                throw vatTypeErr;
            }

            if (responseData?.exception) {
                throw new Error(responseData?.message || 'Validation failed');
            }

            return responseData;
        },

        async createOrder({ state, commit, rootGetters }) {
            const formData = state.formData;
            const { product_id, payment_method } = formData;
            const need_an_invoice = normalizeBooleanFlag(formData.need_an_invoice);

            const defaults = getDefaultFormData();
            // Use allowedFormKeys as source of keys — not Object.keys(formData), so fields present
            // only in defaults / missing from a partial merge (e.g. old localStorage) still serialize.
            let cleanForm = state.allowedFormKeys
                .filter(key => !DISALLOWED_FORM_KEYS.has(key))
                .reduce((obj, key) => {
                    let val = formData[key];
                    if (val === undefined && Object.prototype.hasOwnProperty.call(defaults, key)) {
                        val = defaults[key];
                    }
                    if (val !== undefined) {
                        obj[key] = val;
                    }
                    return obj;
                }, {});
            cleanForm.plate_country =
                formData.plate_country ?? cleanForm.plate_country ?? '';
            cleanForm.category_id = formData.category_id ?? cleanForm.category_id ?? null;
            cleanForm.product_id = formData.product_id ?? cleanForm.product_id ?? null;
            const labels = productCatalogLabels(
                state.productCategories,
                cleanForm.category_id,
                cleanForm.product_id
            );
            cleanForm.vehicle_type = labels.categoryTitle || String(cleanForm.category_id ?? '');
            cleanForm.crossing_type = labels.productTitle || String(cleanForm.product_id ?? '');

            cleanForm.need_an_invoice = need_an_invoice;

            if (!need_an_invoice) {
                clearInvoicePayload(cleanForm);
            }
            normalizeInvoiceVatNumber(cleanForm);
            normalizeLicensePlatesInForm(cleanForm);

            const currencyCode = String(
                rootGetters?.['currency/currentCurrency']?.code
                || selectedCurrencyCodeFromStorage()
                || ''
            )
                .trim()
                .toUpperCase();

            const orderMainForm = buildOrderMainForm(cleanForm, need_an_invoice);
            const billingData = buildBillingData(cleanForm);

            const orderData = {
                products: [{ id: product_id, quantity: 1 }],
                currency: currencyCode || 'EUR',
                payment_method,
                form: {
                    main: orderMainForm
                },
                billing_data: Object.keys(billingData).length ? billingData : null,
                api_tags: await buildOrderApiTags(cleanForm.email)
            };

            let res;
            try {
                res = await new ApiClient().request({
                    method: 'POST',
                    path: '/orders/make',
                    data: orderData
                });
            } catch (e) {
                const validationErr = validationErrorFromResponseData(e?.response?.data);
                if (validationErr) throw validationErr;
                throw e;
            }

            const validationErr = validationErrorFromResponseData(res?.data);
            if (validationErr) throw validationErr;

            commit('SET_ORDER_DATA', res.data);
            return res.data;
        },

        /**
         * POST /orders/amount — product total + payment-method fee for checkout display.
         * @param {string|null} [paymentMethodOverride]
         */
        async fetchOrderAmount({ state, commit, rootGetters }, paymentMethodOverride = null) {
            const payment_method =
                paymentMethodOverride != null && paymentMethodOverride !== ''
                    ? paymentMethodOverride
                    : state.formData?.payment_method;
            if (payment_method == null || payment_method === '') {
                commit('SET_ORDER_AMOUNT', null);
                return null;
            }

            const product_id = state.formData?.product_id;
            if (product_id == null || product_id === '') {
                commit('SET_ORDER_AMOUNT', null);
                return null;
            }

            commit('SET_ORDER_AMOUNT_LOADING', true);
            try {
                const currency = resolveCheckoutCurrencyCode(rootGetters)
                    || selectedCurrencyCodeFromStorage()
                    || 'EUR';
                const res = await new ApiClient().request({
                    method: 'POST',
                    path: '/orders/amount',
                    data: {
                        products: [{ id: product_id, quantity: 1 }],
                        payment_method,
                        currency,
                    },
                });

                const data = extractOrderAmountFromApiEnvelope(res);
                if (!data) {
                    commit('SET_ORDER_AMOUNT', null);
                    return null;
                }

                const amount = Number(data.amount);
                const sub_amount = Number(data.sub_amount);
                const payment_method_fee = Number(data.payment_method_fee);
                const payload = {
                    amount: Number.isFinite(amount) ? amount : 0,
                    sub_amount: Number.isFinite(sub_amount) ? sub_amount : 0,
                    payment_method_fee: Number.isFinite(payment_method_fee) ? payment_method_fee : 0,
                    currency: data.currency ?? currency ?? null,
                    payment_method,
                };
                commit('SET_ORDER_AMOUNT', payload);
                return payload;
            } catch (e) {
                console.warn('[form] fetchOrderAmount failed', e);
                commit('SET_ORDER_AMOUNT', null);
                throw e;
            } finally {
                commit('SET_ORDER_AMOUNT_LOADING', false);
            }
        },

        /**
         * Refetch GET /countries (via POST /api) using current language id for translated names.
         */
        async reloadCountries({ commit, dispatch, rootState }) {
            const api = new ApiClient();
            const res = await api.request({ path: '/countries' });
            const countries = (res.data.data || []).map(c => normalizeFlagEntity({
                code: c.code,
                dial_code: c.dial_code,
                flag: c.flag,
                translated: { name: c.translated?.name || c.code }
            }));
            commit('SET_COUNTRIES', countries);
            commit('SET_COUNTRIES_FETCH_LANGUAGE_ID', rootState.lang?.id ?? null);
            await dispatch('syncVehicleCountryToLanguage');
            return countries;
        },

        async loadProductCatalog({ commit }) {
            const catalog = await fetchProductCatalog();
            commit('SET_PRODUCT_CATALOG', catalog.categories);
            return catalog;
        },

        /**
         * Loads countries, products, form fields, payment methods once per session
         * (deduped across all components). Pass { force: true } to refetch.
         */
        async loadInitialData({ commit, state, dispatch, rootState }, options = {}) {
            const force = options && options.force === true;
            if (force) {
                initialDataLoaded = false;
                initialDataLoadPromise = null;
            }
            if (!force && initialDataLoaded) {
                const currentLangId = rootState.lang?.id;
                const fetchedFor = state.countriesFetchLanguageId;
                const hasCountries = state.countries && state.countries.length;
                const langMismatch =
                    hasCountries
                    && currentLangId != null
                    && (fetchedFor == null
                        || String(fetchedFor) !== String(currentLangId));
                if (langMismatch) {
                    return Promise.all([
                        dispatch('reloadCountries'),
                        dispatch('loadProductCatalog')
                    ]).then(() => dispatch('syncVehicleCountryToLanguage'));
                }
                const hasCatalog = state.productCategories && state.productCategories.length;
                if (!hasCatalog) {
                    return dispatch('loadProductCatalog').then(() =>
                        dispatch('syncVehicleCountryToLanguage')
                    );
                }
                return dispatch('syncVehicleCountryToLanguage');
            }
            if (!force && initialDataLoadPromise) {
                return initialDataLoadPromise;
            }

            const run = async () => {
                const api = new ApiClient();
                const langIdForCountries = rootState.lang?.id ?? null;

                /**
                 * Skip GET /countries when SSR bootstrap (window.__APP_DATA__.countries via
                 * store → initState → form/SET_COUNTRIES) already seeded the store for the
                 * current language. Only refetch when the store is empty or was filled for
                 * a different langId.
                 */
                const loadCountries = async () => {
                    const hasFreshCountriesInStore =
                        Array.isArray(state.countries)
                        && state.countries.length > 0
                        && state.countriesFetchLanguageId != null
                        && String(state.countriesFetchLanguageId) === String(langIdForCountries ?? '');
                    if (hasFreshCountriesInStore) {
                        return state.countries;
                    }
                    const res = await api.request({ path: '/countries' });
                    const countries = (res.data.data || []).map(c => normalizeFlagEntity({
                        code: c.code,
                        dial_code: c.dial_code,
                        flag: c.flag,
                        translated: { name: c.translated?.name || c.code }
                    }));
                    commit('SET_COUNTRIES', countries);
                    commit('SET_COUNTRIES_FETCH_LANGUAGE_ID', langIdForCountries);
                    return countries;
                };

                const loadFormFields = async (countries) => {
                    const res = await api.request({ path: '/forms/fields' });
                    const formFields = ensureInvoiceVatField(normalizeFormFields((res.data.data || []).map(field => {
                        const f = { ...field };
                        if (f.key === 'vehicle_registration_country') f.key = 'plate_country';
                        if (['plate_country', 'invoice_country'].includes(f.key)) {
                            f.options = countries || [];
                        }
                        return f;
                    })));
                    commit('SET_FORM_FIELDS', formFields);
                    const apiKeys = formFields
                        .map(f => f.key)
                        .filter((key) => !DISALLOWED_FORM_KEYS.has(key));
                    const systemKeys = Object.keys(getDefaultFormData())
                        .filter(key => !apiKeys.includes(key));
                    /** Always allow plate country (API field list can omit it; createOrder must send it). */
                    commit('SET_ALLOWED_FORM_KEYS', [...new Set([...apiKeys, ...systemKeys, 'plate_country'])]);
                    return formFields;
                };

                let countries = [];
                try { countries = await loadCountries(); } catch (e) { /* partial load: skip */ }
                await dispatch('syncVehicleCountryToLanguage');
                try { await dispatch('loadProductCatalog'); } catch (e) { /* partial load: skip */ }
                await dispatch('autoSelectDefaultProduct');
                try { await loadFormFields(countries); } catch (e) { /* partial load: skip */ }
                try {
                    /**
                     * Initial load: plate_country may already be set (restored
                     * from localStorage or auto-synced via
                     * syncVehicleCountryToLanguage above). If empty, the
                     * resolver falls back to the UI-language country.
                     */
                    await loadPaymentMethodsData(
                        commit,
                        state.formData?.plate_country,
                        rootState?.lang?.code,
                        countries,
                    );
                } catch (e) { /* partial load: skip */ }
            };

            initialDataLoadPromise = run()
                .then(() => {
                    const has =
                        (state.countries && state.countries.length) ||
                        (state.products && state.products.length) ||
                        (state.formFields && state.formFields.length);
                    if (has) {
                        initialDataLoaded = true;
                    }
                })
                .finally(() => {
                    initialDataLoadPromise = null;
                });

            return initialDataLoadPromise;
        },

        async loadCategorizedProducts({ commit }) {
            const catalog = await fetchProductCatalog();
            commit('SET_CATEGORIZED_PRODUCTS', catalog.categories);
            return catalog.categories;
        },

        /**
         * Refetch /payments/methods/sorted using the current plate_country
         * from formData. Caches by country code so calling this repeatedly
         * with the same selection is a no-op. Switching plate_country in the
         * vehicle dropdown is the primary trigger for a real refetch.
         */
        async reloadPaymentMethods({ commit, state, rootState }) {
            const key = resolvePaymentCountryCode(
                state.formData?.plate_country,
                rootState?.lang?.code,
                state.countries,
            );
            if (paymentMethodsFetchedForPlateKey === key) return;
            await loadPaymentMethodsData(
                commit,
                state.formData?.plate_country,
                rootState?.lang?.code,
                state.countries,
            );
        },

        async getOrderByToken({ commit }, token) {
            const res = await new ApiClient().request({
                method: 'POST',
                path: `/orders/get/${token}`
            })

            commit('SET_ORDER_DATA', res.data)
            return res.data
        },

        /**
         * Pre-fill the buy-vignette form from a saved/abandoned-order token.
         *
         * Used when the user lands on `/buy-vignette?token=<uuid>` (e.g. resuming
         * a session, recovering an abandoned cart, opening a CMS-shared link).
         * The token is exchanged for a server-side draft via
         * `POST /orders/request-data/<token>`. On success, the draft's `form.main`,
         * the first product's id (mapped to `product_id` + derived `category_id`
         * / `selected_duration`), `payment_method`, and `currency` are committed
         * to the store and persisted to localStorage so the rest of the flow
         * picks up exactly where the user left off.
         *
         * Returns the parsed payload on success, or `null` on any failure
         * (network, 4xx/5xx, missing token, malformed body) — caller should
         * fall back to the default empty-form behaviour.
         */
        async hydrateFromOrderToken({ commit, dispatch, state }, token) {
            const cleanToken = String(token || '').trim();
            if (!cleanToken) return null;

            /**
             * Reuse the in-flight promise when the same token is requested again
             * (e.g. dev-mode HMR remount, or two BuyVignette instances mounting in
             * the same tick). Different token starts a fresh request.
             */
            if (hydrateOrderTokenLastUuid === cleanToken && hydrateOrderTokenPromise) {
                return hydrateOrderTokenPromise;
            }

            hydrateOrderTokenLastUuid = cleanToken;
            hydrateOrderTokenPromise = (async () => {
                let res;
                try {
                    res = await new ApiClient().request({
                        method: 'POST',
                        path: `/orders/request-data/${cleanToken}`,
                    });
                } catch (e) {
                    return null;
                }

                const payload = res?.data?.data ?? res?.data ?? null;
                if (!payload || typeof payload !== 'object') return null;

                const main = payload.form?.main ?? {};
                const products = Array.isArray(payload.products) ? payload.products : [];
                const firstProduct = products[0] || null;
                const productId = firstProduct?.id ?? null;

                const formPatch = { ...main };

                /**
                 * Backend sends `invoice_vat_number: "CZ12345678"` (flat string) but
                 * the form stores VAT as a composite `{ country, number }`. Wrap it
                 * before normalizeFormKeys aliases the key to `invoice_vat`.
                 */
                if (Object.prototype.hasOwnProperty.call(formPatch, 'invoice_vat_number')) {
                    const rawVat = String(formPatch.invoice_vat_number ?? '').trim();
                    formPatch.invoice_vat_number = { country: '', number: rawVat };
                }

                if (productId != null) {
                    formPatch.product_id = productId;
                    /** Derive `category_id` + `selected_duration` from the loaded catalog so
                     *  the step-2 fuel list and step-1 duration radio show the saved choice. */
                    const found = (state.products || []).find(
                        (p) => p.id === productId || String(p.id) === String(productId),
                    );
                    if (found) {
                        const days = extractProductDuration(found);
                        if (days > 0) formPatch.selected_duration = days;
                        const cid = getCategoryId(found);
                        if (cid != null && cid !== '') formPatch.category_id = cid;
                    }
                }

                if (payload.payment_method != null) {
                    formPatch.payment_method = payload.payment_method;
                }

                /**
                 * Restored plate_country must NOT be overwritten by the language-driven
                 * autosync. Clearing the autosync mirror marks this value as user-driven
                 * (same signal as `markPlateCountryManual`).
                 */
                if (formPatch.plate_country) {
                    try {
                        if (typeof localStorage !== 'undefined') {
                            localStorage.removeItem(AUTO_SELECTED_PLATE_COUNTRY_KEY);
                        }
                    } catch (_) { /* ignore */ }
                }

                commit('SET_FORM_DATA', formPatch);
                await dispatch('saveDataToLocalStorage');

                if (payload.currency) {
                    await dispatch('currency/setCurrencyByCode', payload.currency, { root: true });
                }

                if (formPatch.payment_method) {
                    await dispatch('fetchOrderAmount').catch(() => {});
                }

                return payload;
            })().finally(() => {
                hydrateOrderTokenPromise = null;
            });

            return hydrateOrderTokenPromise;
        }

    },

    getters: {
        formData: state => state.formData,
        step: state => state.step,
        stepName: state => STEP_NAMES[state.step - 1] || 'select-vignette',
        countries: state => state.countries,
        productCategories: state => state.productCategories || [],
        products: state => state.products,
        categorizedProducts: state => state.categorizedProducts || [],
        productIconItems: state => state.productIconItems || [],
        formFields: state => state.formFields,
        paymentMethods: state => state.paymentMethods,
        paymentMethodsMain: state => state.paymentMethodsMain || [],
        paymentMethodsOther: state => state.paymentMethodsOther || [],
        stepValidationErrors: state => state.stepValidationErrors || {},
        loading: state => state.loadingCount > 0,
        orderData: state => state.orderData,
        orderAmount: state => state.orderAmount,
        orderAmountLoading: state => state.orderAmountLoading,
    },
};
